/**
 * How a stored `items` row becomes an indexing state — the ONE mapping, shared.
 *
 * Extracted from `server/api/items/status.post.ts` when `/api/activity` (MAR-109) became a second
 * reader of the same columns. A second copy here is exactly the failure the Activity Center exists
 * to end: four independent copies of the indexing error mapping are what put the literal sentinel
 * `quota:indexing` in front of a user. Two endpoints, one function.
 */

/**
 * `quota_blocked` is FIRST-CLASS, not a flavour of `failed`.
 *
 * It used to collapse to `failed`, and `error_message` — which carries a machine-readable sentinel —
 * was passed through raw. Nothing failed: the bytes are fine, the work simply has not been paid for,
 * and it recovers on its own when the period rolls over.
 */
export type IndexState = 'queued' | 'working' | 'awaiting_confirmation' | 'ready' | 'failed' | 'quota_blocked'

/** The states a legacy `status` can map to — every IndexState except the derived `awaiting_confirmation`. */
export type LegacyIndexState = Exclude<IndexState, 'awaiting_confirmation'>

/**
 * Pre-0071 rows only: `status` carried the indexing state and the review lifecycle together.
 *
 * The ONE status → state mapping. `itemPendingGuard.test.ts` fails if a local copy reappears.
 */
export function legacyFromStatus(status: string | null): LegacyIndexState {
  if (status === 'processing') return 'working'
  if (status === 'uploading') return 'queued'
  if (status === 'error') return 'failed'
  if (status === 'quota_blocked') return 'quota_blocked'
  return 'ready'
}

/**
 * The indexing state, read from `items.index_state` (migration 0071) with `items.status` as the
 * fallback for any row written before it existed.
 *
 * `awaiting_confirmation` is deliberately not a column value: a deferred deep read belongs to
 * `ocr_review_sessions`, and duplicating it onto the item would create two places to be wrong. It
 * outranks `ready` here because the document exists but its text is waiting on a human decision —
 * which is the entire reason to surface it.
 */
export function deriveIndexState(indexState: string | null, status: string | null, pendingRead: boolean): IndexState {
  const resolved = indexState ?? legacyFromStatus(status)
  if (resolved === 'working' || resolved === 'queued') return resolved
  if (resolved === 'quota_blocked') return 'quota_blocked'
  if (resolved === 'failed') return 'failed'
  if (pendingRead) return 'awaiting_confirmation'
  return 'ready'
}
